import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { RootState } from '../../store/store';
import { jobSiteService } from '../../services/apiService';
import { JobSiteWithStats, UpdateJobSiteRequest } from '../../store/interfaces';
import './jobSiteDetails.scss';

const JobSiteDetailsPage: React.FC = () => {
  const params = useParams();
  const user = useSelector((state: RootState) => state.user.user);
  const [jobSite, setJobSite] = useState<JobSiteWithStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [editingShortName, setEditingShortName] = useState(false);
  const [shortNameValue, setShortNameValue] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    const fetchJobSiteDetails = async () => {
      if (!user || !user.companyId || !params.jobSiteId) {
        setError('Missing required parameters');
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        const data = await jobSiteService.getJobSiteWithStats(
          user,
          user.companyId,
          Number(params.jobSiteId)
        );
        setJobSite(data);
        setShortNameValue(data.shortName || '');
        setError(null);
      } catch (error) {
        console.error('Failed to fetch job site details:', error);
        setError('Failed to load job site details');
      } finally {
        setLoading(false);
      }
    };

    fetchJobSiteDetails();
  }, [user, params.jobSiteId]);

  const handleSaveShortName = async () => {
    if (!jobSite || !user) return;

    try {
      setSaving(true);
      const updateData: UpdateJobSiteRequest = {
        shortName: shortNameValue.trim() || undefined
      };

      await jobSiteService.updateJobSite(user, jobSite.id, updateData);

      // Update local state
      setJobSite({
        ...jobSite,
        shortName: shortNameValue.trim() || undefined
      });

      setEditingShortName(false);
    } catch (error) {
      console.error('Failed to update short name:', error);
      setError('Failed to update short name');
    } finally {
      setSaving(false);
    }
  };

  const handleCancelEdit = () => {
    setShortNameValue(jobSite?.shortName || '');
    setEditingShortName(false);
  };

  if (loading) {
    return (
      <div className="job-site-details-container">
        <div className="loading">Loading job site details...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="job-site-details-container">
        <div className="error">{error}</div>
      </div>
    );
  }

  if (!jobSite) {
    return (
      <div className="job-site-details-container">
        <div className="error">Job site not found</div>
      </div>
    );
  }

  const formatTime = (timeString: string) => {
    return new Date(timeString).toLocaleTimeString();
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString();
  };



  const calculateHours = (startTime: string, endTime?: string) => {
    const start = new Date(startTime);
    const end = endTime ? new Date(endTime) : new Date();
    const diffMs = end.getTime() - start.getTime();
    const diffHours = diffMs / (1000 * 60 * 60);
    return diffHours;
  };

  const getWorkTypeName = (work: any, index: number) => {
    // Check if this is a break using the new breakType field
    const isBreak = work.breakType && work.breakType.toLowerCase() !== 'none' && work.breakType.toLowerCase() !== 'null';

    if (isBreak) {
      return `${work.breakType} Break`;
    }
    return work.costCodeName;
  };

  return (
    <div className="job-site-details-container">
      <div className="header">
        <h1>{jobSite.name}</h1>
        <div className="stats">
                    <span className="stat">
            <strong>{jobSite.currentEmployeeCount}</strong> employees
          </span>

          <span className="stat">
            <strong>{jobSite.activeCostCodes.length}</strong> cost codes
          </span>
        </div>
      </div>

      <div className="content">
        {/* Job Site Information Section */}
        <section className="job-site-info-section">
          <h2>Job Site Information</h2>
          <div className="info-grid">
            <div className="info-item">
              <label>Full Name:</label>
              <span>{jobSite.name}</span>
            </div>

            <div className="info-item">
              <label>Short Name:</label>
              {editingShortName ? (
                <div className="edit-controls">
                  <input
                    type="text"
                    value={shortNameValue}
                    onChange={(e) => setShortNameValue(e.target.value)}
                    placeholder="Enter short name"
                    className="edit-input"
                  />
                  <div className="edit-buttons">
                    <button
                      onClick={handleSaveShortName}
                      disabled={saving}
                      className="save-btn"
                    >
                      {saving ? 'Saving...' : 'Save'}
                    </button>
                    <button
                      onClick={handleCancelEdit}
                      disabled={saving}
                      className="cancel-btn"
                    >
                      Cancel
                    </button>
                  </div>
                </div>
              ) : (
                <div className="display-controls">
                  <span>{jobSite.shortName || 'Not set'}</span>
                  <button
                    onClick={() => setEditingShortName(true)}
                    className="edit-btn"
                  >
                    Edit
                  </button>
                </div>
              )}
            </div>

            <div className="info-item">
              <label>Address:</label>
              <span>{jobSite.address || 'Not set'}</span>
            </div>

            <div className="info-item">
              <label>City:</label>
              <span>{jobSite.city || 'Not set'}</span>
            </div>

            <div className="info-item">
              <label>State:</label>
              <span>{jobSite.state || 'Not set'}</span>
            </div>

            <div className="info-item">
              <label>ZIP Code:</label>
              <span>{jobSite.zip || 'Not set'}</span>
            </div>
          </div>
        </section>

        {/* Current Employees Section */}
        <section className="employees-section">
          <h2>Current Employees ({jobSite.currentEmployees.length})</h2>
          {jobSite.currentEmployees.length > 0 ? (
            <div className="employees-grid">
              {jobSite.currentEmployees.map((employee) => (
                <div key={employee.id} className="employee-card">
                  <div className="employee-header">
                    <h3>{employee.name}</h3>
                    <span className="work-details-count">
                      {employee.workDetails.length} work entries
                    </span>
                  </div>

                  {employee.workDetails.length > 0 && (
                    <div className="work-details">
                      <h4>Work Details:</h4>
                      <div className="work-table-container">
                        <table className="work-table">
                          <thead>
                            <tr>
                              <th>Type</th>
                              <th>Date</th>
                              <th>Start Time</th>
                              <th>End Time</th>
                              <th>Hours</th>
                            </tr>
                          </thead>
                          <tbody>
                            {employee.workDetails.map((work, index) => {
                              const isLastEntry = index === employee.workDetails.length - 1;
                              const hasNoEndTime = !work.endTime || work.endTime === work.startTime;
                              const isActive = isLastEntry && hasNoEndTime;
                              const hours = isActive
                                ? calculateHours(work.startTime)
                                : (typeof work.hours === 'string' ? parseFloat(work.hours) : work.hours);

                              return (
                                <tr key={work.id} className={isActive ? 'active-entry' : ''}>
                                  <td className="work-type">
                                    <strong>{getWorkTypeName(work, index)}</strong>
                                  </td>
                                  <td>{formatDate(work.startTime)}</td>
                                  <td>{formatTime(work.startTime)}</td>
                                  <td className={hasNoEndTime ? 'no-end-time' : ''}>
                                    {hasNoEndTime ? 'Active' : formatTime(work.endTime)}
                                  </td>
                                  <td className={`hours ${isActive ? 'active-hours' : ''}`}>
                                    {hours.toFixed(2)} hours
                                  </td>
                                </tr>
                              );
                            })}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>
          ) : (
            <p className="no-data">No employees currently working</p>
          )}
        </section>



        {/* Active Cost Codes Section */}
        <section className="cost-codes-section">
          <h2>Active Cost Codes ({jobSite.activeCostCodes.length})</h2>
          {jobSite.activeCostCodes.length > 0 ? (
            <div className="cost-codes-list">
              {jobSite.activeCostCodes.map((costCode) => (
                <div key={costCode.id} className="cost-code-item">
                  {costCode.name}
                </div>
              ))}
            </div>
          ) : (
            <p className="no-data">No active cost codes</p>
          )}
        </section>
      </div>
    </div>
  );
};

export default JobSiteDetailsPage; 